import * as React from "react";
import { RouterLink as NextLink } from "../../router";
import { Card, CardContent } from "../../card";
import { StatCard, type StatFooterItem } from "../stat-card";
import type { Intent } from "../../tokens/intents";

/**
 * StatStrip — a row of BARE metrics inside the shared card frame.
 *
 * The bare `<StatCard>` is the right *metric* (the number leads, no tile), but a
 * naked row of them reads as "floating numbers" wherever it sits above framed
 * content (a kanban board, a DataTable). StatStrip keeps the metric bare and
 * gives the *row* one frame from the same surface family the kanban cards and
 * DataTable already use (soft tint + hairline, no shadow) — so the strip reads
 * as one finished, anchored component instead of loose numbers.
 *
 *   - `variant="standalone" | "board"` → a framed `<Card>` band. `board` adds a
 *     leading status-intent dot per cell (the pipeline cue) via the StatCard
 *     `leadingDot` prop. Use above a board / as a dashboard panel.
 *   - `variant="table"` → a frameless band of cells sealed with `border-b
 *     border-border`, meant to live INSIDE a DataTable's own Card as its caption
 *     header. Pass it to `<DataTable summary={…} />` (which renders it flush
 *     above the column-header row) — the strip becomes the table's caption.
 *
 * Composes the unchanged `<StatCard>` markup — it does NOT fork the atom or
 * re-box the metric. See docs/design/MINIMAL-SURFACES-PRD.md and the
 * /styleguide/stat-strips prototype.
 */

export type StatStripItem = {
  title: string;
  value: string;
  description?: string;
  footer?: StatFooterItem[];
  /** Wraps the cell in a link with the shared row hover affordance. */
  href?: string;
  /** `variant="board"`: a leading status-intent dot (the pipeline cue). */
  intent?: Intent;
  // Trend / sparkline passthrough (same as StatCard) for richer cells.
  trend?: number;
  trendGood?: "up" | "down" | "none";
  trendLabel?: string;
  sparkData?: number[];
  accentColor?: string;
};

export interface StatStripProps {
  items: StatStripItem[];
  variant?: "standalone" | "board" | "table";
  /** Optional flush title header above the metrics (standalone / board only). */
  title?: React.ReactNode;
  className?: string;
}

function StatStripCell({
  item,
  variant,
}: {
  item: StatStripItem;
  variant: "standalone" | "board" | "table";
}) {
  const metric = (
    // `table` cells flatten to px-4 to share the table atoms' 16px cell inset
    // (table.tsx) — the caption band's first cell must sit on the same left
    // edge as the column below it. Standalone/board strips keep the card px-6.
    <div className={variant === "table" ? "px-4 py-4" : "px-6 py-4"}>
      <StatCard
        title={item.title}
        value={item.value}
        description={item.description}
        footer={item.footer}
        leadingDot={variant === "board" ? item.intent : undefined}
        trend={item.trend}
        trendGood={item.trendGood}
        trendLabel={item.trendLabel}
        sparkData={item.sparkData}
        accentColor={item.accentColor}
      />
    </div>
  );

  if (item.href) {
    return (
      <NextLink
        href={item.href}
        className="min-w-0 flex-1 transition-colors hover:bg-secondary"
      >
        {metric}
      </NextLink>
    );
  }
  return <div className="min-w-0 flex-1">{metric}</div>;
}

export function StatStrip({
  items,
  variant = "standalone",
  title,
  className,
}: StatStripProps) {
  // Cells share width equally and stack on narrow viewports; the responsive
  // divider (hairline below when stacked → hairline between when in a row) is
  // owned here so every adopter gets identical wrap behavior. Uses the viewport
  // `sm:` breakpoint (not a container query) so the framed row works on every
  // page whether or not it sits inside an `@container/page`.
  const cells = (
    <div className="flex flex-col divide-y divide-border/40 sm:flex-row sm:divide-x sm:divide-y-0">
      {items.map((item, i) => (
        <StatStripCell key={`${item.title}-${i}`} item={item} variant={variant} />
      ))}
    </div>
  );

  if (variant === "table") {
    // Lives inside the table's own Card. The band's bottom border is a
    // full-opacity seal matching the DataTable's own edge token
    // (data-table.tsx) so band edge == table edge — no /60 mismatch.
    return (
      <div className={["border-b border-border", className].filter(Boolean).join(" ")}>
        {cells}
      </div>
    );
  }

  return (
    <Card className={["gap-0 overflow-hidden py-0", className].filter(Boolean).join(" ")}>
      <CardContent className="px-0">
        {title ? (
          <div className="border-b border-border/60 px-6 py-3">
            <span className="type-body font-medium text-foreground">{title}</span>
          </div>
        ) : null}
        {cells}
      </CardContent>
    </Card>
  );
}
